// =============================================================================
// CURRENCY UTILITIES (Brazilian Real)
// =============================================================================

/**
 * Format cents to Brazilian Real (R$ 1.234,56)
 */
export function formatCurrency(cents: number): string {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(cents / 100);
}

/**
 * Format cents to simple number (1.234,56)
 */
export function formatCurrencyValue(cents: number): string {
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(cents / 100);
}

/**
 * Parse Brazilian currency string to cents
 * Accepts: "R$ 1.234,56", "1.234,56", "1234.56", "1234,56"
 */
export function parseCurrencyToCents(value: string): number {
    // Remove R$ and spaces
    let cleaned = value.replace(/R\$\s*/g, '').trim();

    // Check if it's using Brazilian format (comma as decimal separator)
    if (cleaned.includes(',')) {
        // Remove thousand separators and convert comma to dot
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }

    const number = parseFloat(cleaned);
    if (isNaN(number)) {
        throw new Error(`Invalid currency value: ${value}`);
    }

    return Math.round(number * 100);
}

/**
 * Calculate percentage of a value in cents
 */
export function calculatePercentage(cents: number, percent: number): number {
    return Math.round((cents * percent) / 100);
}

/**
 * Calculate affiliate discount (10%)
 */
export function calculateAffiliateDiscount(grossCents: number): number {
    return calculatePercentage(grossCents, 10);
}

/**
 * Calculate affiliate commission (10% of net paid)
 */
export function calculateAffiliateCommission(netPaidCents: number): number {
    return calculatePercentage(netPaidCents, 10);
}

/**
 * Calculate net amount after discount and fees
 */
export function calculateNetAmount(
    grossCents: number,
    discountCents: number,
    feeCents: number
): number {
    return grossCents - discountCents - feeCents;
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`.replace('.', ',');
}

/**
 * Calculate monthly value from yearly
 */
export function yearlyToMonthly(yearlyCents: number): number {
    return Math.round(yearlyCents / 12);
}

/**
 * Calculate yearly value from monthly
 */
export function monthlyToYearly(monthlyCents: number): number {
    return monthlyCents * 12;
}

/**
 * Calculate savings with yearly billing (usually 2 months free)
 */
export function calculateYearlySavings(
    monthlyCents: number,
    yearlyDiscountPercent: number = 20
): {
    regularYearly: number;
    discountedYearly: number;
    savings: number;
} {
    const regularYearly = monthlyToYearly(monthlyCents);
    const discountedYearly = regularYearly - calculatePercentage(regularYearly, yearlyDiscountPercent);
    return {
        regularYearly,
        discountedYearly,
        savings: regularYearly - discountedYearly
    };
}
